import {
  Ban,
  Brush,
  Bug,
  BugOff,
  Check,
  CircleCheck,
  CircleHelp,
  CircleSlash,
  CircleX,
  Clock,
  Eye,
  FlaskConical,
  ListOrdered,
  LoaderCircle,
  Minus,
  RotateCcw,
  SearchCheck,
  Shield,
  ShieldAlert,
  SkipForward,
  Square,
  TimerOff,
  TriangleAlert,
  Wrench,
  X,
  ZapOff,
  type LucideIcon,
} from "lucide-react";
import type {
  AgentSource,
  ArtifactState,
  CleanupState,
  ExecutionLifecycle,
  FailureLayer,
  Outcome,
  OutcomeCounts,
  Role,
  RunCounts,
  RunLifecycle,
  RunSnapshot,
  RunSummary,
  UserStatus,
  Verification,
  WaitReason,
} from "@/api/types";
import { cellOutcome, type CellOutcome } from "./cells";
import { plural } from "./format";

export type Tone =
  | "green"
  | "red"
  | "orange"
  | "orange-soft"
  | "amber"
  | "violet"
  | "slate"
  | "slate-soft"
  | "zinc"
  | "yellow"
  | "gray"
  | "indigo"
  | "cyan"
  | "pink"
  | "teal"
  | "sky"
  | "purple"
  | "blue";

export const TONE_CLASS: Record<Tone, string> = {
  green: "bg-tone-green-bg text-tone-green-fg border-tone-green-border",
  red: "bg-tone-red-bg text-tone-red-fg border-tone-red-border",
  orange: "bg-tone-orange-bg text-tone-orange-fg border-tone-orange-border",
  "orange-soft": "bg-tone-orange-soft-bg text-tone-orange-soft-fg border-tone-orange-soft-border",
  amber: "bg-tone-amber-bg text-tone-amber-fg border-tone-amber-border",
  violet: "bg-tone-violet-bg text-tone-violet-fg border-tone-violet-border",
  slate: "bg-tone-slate-bg text-tone-slate-fg border-tone-slate-border",
  "slate-soft": "bg-tone-slate-soft-bg text-tone-slate-soft-fg border-tone-slate-soft-border",
  zinc: "bg-tone-zinc-bg text-tone-zinc-fg border-tone-zinc-border",
  yellow: "bg-tone-yellow-bg text-tone-yellow-fg border-tone-yellow-border",
  gray: "bg-tone-gray-bg text-tone-gray-fg border-tone-gray-border",
  indigo: "bg-tone-indigo-bg text-tone-indigo-fg border-tone-indigo-border",
  cyan: "bg-tone-cyan-bg text-tone-cyan-fg border-tone-cyan-border",
  pink: "bg-tone-pink-bg text-tone-pink-fg border-tone-pink-border",
  teal: "bg-tone-teal-bg text-tone-teal-fg border-tone-teal-border",
  sky: "bg-tone-sky-bg text-tone-sky-fg border-tone-sky-border",
  purple: "bg-tone-purple-bg text-tone-purple-fg border-tone-purple-border",
  blue: "bg-tone-blue-bg text-tone-blue-fg border-tone-blue-border",
};

export const TONE_FILL: Record<Tone, string> = {
  green: "bg-tone-green-fg",
  red: "bg-tone-red-fg",
  orange: "bg-tone-orange-fg",
  "orange-soft": "bg-tone-orange-soft-fg",
  amber: "bg-tone-amber-fg",
  violet: "bg-tone-violet-fg",
  slate: "bg-tone-slate-fg",
  "slate-soft": "bg-tone-slate-soft-fg",
  zinc: "bg-tone-zinc-fg",
  yellow: "bg-tone-yellow-fg",
  gray: "bg-tone-gray-fg",
  indigo: "bg-tone-indigo-fg",
  cyan: "bg-tone-cyan-fg",
  pink: "bg-tone-pink-fg",
  teal: "bg-tone-teal-fg",
  sky: "bg-tone-sky-fg",
  purple: "bg-tone-purple-fg",
  blue: "bg-tone-blue-fg",
};

export interface StatusMeta {
  label: string;
  tone: Tone;
  icon: LucideIcon;
  spin?: boolean;
}

export const OUTCOME_META: Record<Outcome, StatusMeta> = {
  PASSED: { label: "Passed", tone: "green", icon: CircleCheck },
  FAILED: { label: "Failed", tone: "red", icon: CircleX },
  ERROR: { label: "Error", tone: "orange", icon: TriangleAlert },
  TIMED_OUT: { label: "Timed out", tone: "amber", icon: TimerOff },
  BLOCKED: { label: "Blocked", tone: "violet", icon: Ban },
  SKIPPED: { label: "Skipped", tone: "slate", icon: SkipForward },
  UNSUPPORTED: { label: "Unsupported", tone: "slate-soft", icon: CircleSlash },
  CANCELLED: { label: "Cancelled", tone: "zinc", icon: Square },
  INTERRUPTED: { label: "Interrupted", tone: "yellow", icon: ZapOff },
  PRECONDITION_FAILED: { label: "Precondition failed", tone: "orange-soft", icon: ShieldAlert },
  KNOWN_DEFECT: { label: "Known defect", tone: "teal", icon: Bug },
  UNKNOWN: { label: "Pending result", tone: "gray", icon: CircleHelp },
};

export const CELL_OUTCOME_META: Record<CellOutcome, StatusMeta> = {
  ...OUTCOME_META,
  EXCLUDED: { label: "Excluded", tone: "slate-soft", icon: Minus },
};

export type KnownDefectState = "known" | "partly_fixed" | "fixed";

export const KNOWN_DEFECT_META: Record<KnownDefectState, StatusMeta> = {
  known: { label: "Known defect", tone: "teal", icon: Bug },
  partly_fixed: { label: "Partly fixed", tone: "sky", icon: Wrench },
  fixed: { label: "Defect fixed", tone: "green", icon: BugOff },
};

export function knownDefectState(
  outcome: string,
  kd: { fixed: boolean; now_passing: string[] } | null | undefined,
): KnownDefectState | null {
  if (!kd) return null;
  if (kd.fixed) return "fixed";
  if (outcome !== "KNOWN_DEFECT") return null;
  return kd.now_passing.length > 0 ? "partly_fixed" : "known";
}

export function knownDefectNote(c: { known_defect?: number } | undefined): string | null {
  const n = c?.known_defect ?? 0;
  return n > 0 ? plural(n, "known defect") : null;
}

export const PROBLEM_OUTCOMES: readonly Outcome[] = ["FAILED", "ERROR", "PRECONDITION_FAILED", "INTERRUPTED", "TIMED_OUT"];

export function isProblemOutcome(o: string): boolean {
  return (PROBLEM_OUTCOMES as readonly string[]).includes(o);
}

export const EXEC_LIFECYCLE_META: Record<ExecutionLifecycle, StatusMeta> = {
  PENDING: { label: "Pending", tone: "gray", icon: Clock },
  QUEUED: { label: "Queued", tone: "slate", icon: ListOrdered },
  RUNNING: { label: "Running", tone: "blue", icon: LoaderCircle, spin: true },
  VERIFYING: { label: "Verifying", tone: "sky", icon: SearchCheck },
  RECOVERING: { label: "Recovering", tone: "amber", icon: RotateCcw },
  CLEANUP: { label: "Cleaning up", tone: "slate", icon: Brush },
  COMPLETED: { label: "Completed", tone: "gray", icon: Check },
};

export const CLEANUP_META: Record<CleanupState, StatusMeta> = {
  NOT_STARTED: { label: "Not started", tone: "gray", icon: Minus },
  RUNNING: { label: "Running", tone: "blue", icon: LoaderCircle, spin: true },
  SUCCEEDED: { label: "Succeeded", tone: "green", icon: Check },
  PARTIAL: { label: "Partial", tone: "amber", icon: TriangleAlert },
  FAILED: { label: "Failed", tone: "red", icon: X },
};

export const RUN_LIFECYCLE_LABEL: Record<RunLifecycle, string> = {
  CREATED: "Created",
  VALIDATING: "Validating",
  RUNNING: "Running",
  RECOVERING: "Recovering",
  FINALIZING: "Finalizing",
  COMPLETED: "Completed",
};

export const WAIT_REASON_LABEL: Record<WaitReason, string> = {
  DEPENDENCY: "Waiting for dependency",
  RESOURCES: "Waiting for resources",
  ENVIRONMENT: "Waiting for environment",
  CAPACITY: "Waiting for resources",
  READY: "Ready to dispatch",
};

export const FAILURE_LAYER_META: Record<FailureLayer, { label: string; tone: Tone }> = {
  AGENT: { label: "Agent", tone: "red" },
  TEST_DEFINITION: { label: "Test definition", tone: "pink" },
  ENVIRONMENT: { label: "Environment", tone: "orange" },
  INFRASTRUCTURE: { label: "Infrastructure", tone: "amber" },
  TELEMETRY: { label: "Telemetry", tone: "cyan" },
  ATATUS_BACKEND: { label: "Issue in Atatus backend", tone: "indigo" },
  PRECONDITION: { label: "Precondition", tone: "yellow" },
  ATS_PLATFORM: { label: "ATS platform", tone: "purple" },
  ISOLATION: { label: "Isolation", tone: "teal" },
  INTEGRATION: { label: "Integration", tone: "sky" },
  UNKNOWN: { label: "Unknown", tone: "gray" },
};

export const FAILURE_TYPE_META: Record<string, { title: string; explanation: string }> = {
  "AGENT.INSTALL_FAILED": {
    title: "Agent could not be installed",
    explanation: "The agent build failed to install into the test application.",
  },
  "AGENT.RUNTIME_ERROR": {
    title: "Agent crashed or errored while running",
    explanation: "The agent raised an error inside the test application.",
  },
  "AGENT.MISSING_TELEMETRY": {
    title: "Agent did not send expected data",
    explanation: "The agent should have sent telemetry to the mock collector but nothing arrived.",
  },
  "AGENT.INCORRECT_TELEMETRY": {
    title: "Agent sent wrong data",
    explanation: "The agent sent telemetry, but its content did not match what the test expects.",
  },
  "AGENT.UNEXPECTED_TELEMETRY": {
    title: "Agent sent data it should not have",
    explanation: "The agent sent telemetry in a situation where it was expected to stay silent, for example after the backend blocked it.",
  },
  "AGENT.LOG_EXPECTATION": {
    title: "Agent logs did not match",
    explanation: "The agent's log output did not contain (or unexpectedly contained) what the test checks for.",
  },
  "AGENT.APP_IMPACTED": {
    title: "Agent changed how the app responds",
    explanation: "With the agent, the application answered with a different status, a shorter body or more latency than the test allows.",
  },
  "TEST_DEFINITION.INVALID_DEFINITION": {
    title: "Test is written incorrectly",
    explanation: "The test definition is invalid, so it could not run. This is not an agent problem.",
  },
  "TEST_DEFINITION.LOCKED_SETTING": {
    title: "Test changes a locked setting",
    explanation: "The test tried to override an agent setting that ATS does not allow tests to change.",
  },
  "TEST_DEFINITION.UNKNOWN_CREDENTIAL": {
    title: "Test uses an unknown credential",
    explanation: "The test refers to a credential that is not configured.",
  },
  "TEST_DEFINITION.INVALID_ASSERTION": {
    title: "A check in the test is broken",
    explanation: "An assertion could not be evaluated because it is defined incorrectly.",
  },
  "ENVIRONMENT.APP_UNREACHABLE": {
    title: "Test application not reachable",
    explanation: "ATS could not connect to the sample application it was testing.",
  },
  "ENVIRONMENT.VARIANT_BUILD_FAILED": {
    title: "App variant image could not be built",
    explanation:
      "ATS could not build the test's app variant image: pulling the Node.js image, installing an npm pin or copying the app failed. This is not an agent problem.",
  },
  "INFRASTRUCTURE.CONTAINER_START_FAILURE": {
    title: "Container failed to start",
    explanation: "A Docker container needed by the test did not start.",
  },
  "INFRASTRUCTURE.REGISTRY_UNAVAILABLE": {
    title: "Image registry unavailable",
    explanation: "ATS could not pull a required image from the registry.",
  },
  "INFRASTRUCTURE.DATASTORE_FAILURE": {
    title: "Test database failed",
    explanation: "A database the test depends on failed to start or respond.",
  },
  "INFRASTRUCTURE.DOCKER_ERROR": { title: "Docker reported an error", explanation: "Docker returned an error while setting up the test." },
  "PRECONDITION.OC_SANITY_FAILED": {
    title: "ObservableCommerce sanity check failed",
    explanation: "The ObservableCommerce checks did not pass, so the test was not started.",
  },
  "PRECONDITION.DOCKER_UNAVAILABLE": {
    title: "Docker is not available",
    explanation: "Docker is not running or not reachable on this machine.",
  },
  "PRECONDITION.ARTIFACT_HASH_MISMATCH": {
    title: "Agent build changed since it was verified",
    explanation: "The agent file does not match the checksum recorded when it was verified.",
  },
  "PRECONDITION.FIXTURE_UNRESOLVED": {
    title: "Test data could not be prepared",
    explanation: "A fixture required by the test could not be resolved.",
  },
  "ATS_PLATFORM.MOCK_UNAVAILABLE": {
    title: "Mock collector unavailable",
    explanation: "The mock Atatus collector stopped responding during the test.",
  },
  "ATS_PLATFORM.OVERLAY_VERSION_MISMATCH": {
    title: "Mismatched ATS component versions",
    explanation: "The agent version found in the build differs from the version the build declares.",
  },
  "ATS_PLATFORM.ENGINE_CRASH": { title: "ATS engine crashed", explanation: "The ATS engine stopped unexpectedly while running the test." },
  "ATS_PLATFORM.INTERNAL": { title: "Internal ATS error", explanation: "ATS hit an unexpected internal problem." },
  "TELEMETRY.NOT_RECEIVED": { title: "Telemetry not received", explanation: "Expected telemetry was not received." },
  "UNKNOWN.UNCLASSIFIED": {
    title: "Failure could not be classified",
    explanation: "ATS could not work out the cause. Check the logs and requests.",
  },
  "ISOLATION.RESOURCE_CONFLICT": {
    title: "Tests clashed over a shared resource",
    explanation: "Two tests tried to use the same resource at the same time.",
  },
};

export function failureTypeMeta(type: string): { title: string; explanation: string } {
  return FAILURE_TYPE_META[type] ?? { title: humanizeId(type.split(".").pop() ?? type), explanation: "" };
}

export function humanizeId(id: string): string {
  const words = id.replace(/[-_]+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export type RuntimeState =
  "connected" | "starting" | "recovering" | "not_running" | "incompatible" | "port_conflict" | "permission_blocked" | "unhealthy";

export const RUNTIME_META: Record<RuntimeState, { tone: Tone; text: string }> = {
  connected: { tone: "green", text: "Runtime connected" },
  starting: { tone: "blue", text: "Runtime starting…" },
  recovering: { tone: "amber", text: "Recovering after restart" },
  not_running: { tone: "gray", text: "Runtime not running" },
  incompatible: { tone: "red", text: "Runtime update needed" },
  port_conflict: { tone: "red", text: "Port 7050 in use" },
  permission_blocked: { tone: "amber", text: "Browser blocked localhost" },
  unhealthy: { tone: "red", text: "Runtime unhealthy" },
};

export const ROLE_META: Record<Role, StatusMeta> = {
  ADMIN: { label: "Admin", tone: "purple", icon: Shield },
  TESTER: { label: "Tester", tone: "blue", icon: FlaskConical },
  VIEWER: { label: "Viewer", tone: "gray", icon: Eye },
};

export const USER_STATUS_META: Record<UserStatus, { label: string; tone: Tone }> = {
  pending: { label: "Waiting", tone: "amber" },
  active: { label: "Active", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  deactivated: { label: "Access removed", tone: "zinc" },
};

export const SOURCE_META: Record<AgentSource, { label: string; tone: Tone }> = {
  development: { label: "Development", tone: "sky" },
  master: { label: "Master", tone: "indigo" },
  released: { label: "Released", tone: "green" },
};

export const VERIFICATION_META: Record<Verification | "VERIFYING", { label: string; tone: Tone }> = {
  PASSED: { label: "Verified", tone: "green" },
  FAILED: { label: "Failed verification", tone: "red" },
  VERIFYING: { label: "Verifying", tone: "blue" },
};

export const ARTIFACT_STATE_META: Record<ArtifactState, { label: string; tone: Tone }> = {
  PENDING: { label: "Capturing", tone: "blue" },
  UPLOADING: { label: "Capturing", tone: "blue" },
  AVAILABLE: { label: "Available", tone: "green" },
  PARTIAL: { label: "Partial", tone: "amber" },
  FAILED: { label: "Failed", tone: "red" },
  EXPIRED: { label: "Expired", tone: "gray" },
  DELETED: { label: "Expired", tone: "gray" },
};

export type RunResultKind = "cancelled" | "failed" | "error" | "blocked" | "passed" | "lifecycle";

function currentOutcomes(run: RunSnapshot | RunSummary): Outcome[] | null {
  if (!("cells" in run)) return null;
  return run.cells.map(cellOutcome).filter((o): o is Outcome => o !== "EXCLUDED");
}

export function deriveRunResult(run: RunSnapshot | RunSummary): StatusMeta & { kind: RunResultKind } {
  if (run.lifecycle === "COMPLETED" && run.control_intent === "CANCEL") return { ...OUTCOME_META.CANCELLED, kind: "cancelled" };
  if (run.lifecycle !== "COMPLETED") {
    const meta =
      run.lifecycle === "RECOVERING"
        ? EXEC_LIFECYCLE_META.RECOVERING
        : run.lifecycle === "RUNNING"
          ? EXEC_LIFECYCLE_META.RUNNING
          : EXEC_LIFECYCLE_META.PENDING;
    return { ...meta, label: RUN_LIFECYCLE_LABEL[run.lifecycle], kind: "lifecycle" };
  }
  const outcomes = currentOutcomes(run);
  const c = run.counts;
  const has = (o: Outcome) => (outcomes ? outcomes.includes(o) : false);
  const failed = outcomes ? has("FAILED") : c.failed > 0;
  const error = outcomes
    ? ["ERROR", "TIMED_OUT", "INTERRUPTED", "PRECONDITION_FAILED"].some((o) => has(o as Outcome))
    : c.error + c.timed_out + c.interrupted + c.precondition_failed > 0;
  const blocked = outcomes ? has("BLOCKED") : c.blocked > 0;
  if (failed) return { ...OUTCOME_META.FAILED, kind: "failed" };
  if (error) return { ...OUTCOME_META.ERROR, kind: "error" };
  if (blocked) return { ...OUTCOME_META.BLOCKED, kind: "blocked" };
  const allPassed = outcomes
    ? outcomes.every((o) => o === "PASSED" || o === "KNOWN_DEFECT" || o === "SKIPPED" || o === "UNSUPPORTED")
    : c.passed + (c.known_defect ?? 0) > 0;
  if (allPassed) return { ...OUTCOME_META.PASSED, kind: "passed" };
  return { ...EXEC_LIFECYCLE_META.COMPLETED, label: RUN_LIFECYCLE_LABEL.COMPLETED, kind: "lifecycle" };
}

export const SEGMENTS: Array<{ key: keyof RunCounts; outcome: Outcome | "RUNNING" | "QUEUED"; label: string; noun?: boolean }> = [
  { key: "passed", outcome: "PASSED", label: "passed" },
  { key: "known_defect", outcome: "KNOWN_DEFECT", label: "known defect", noun: true },
  { key: "failed", outcome: "FAILED", label: "failed" },
  { key: "error", outcome: "ERROR", label: "error" },
  { key: "timed_out", outcome: "TIMED_OUT", label: "timed out" },
  { key: "interrupted", outcome: "INTERRUPTED", label: "interrupted" },
  { key: "precondition_failed", outcome: "PRECONDITION_FAILED", label: "precondition failed" },
  { key: "blocked", outcome: "BLOCKED", label: "blocked" },
  { key: "skipped", outcome: "SKIPPED", label: "skipped" },
  { key: "cancelled", outcome: "CANCELLED", label: "cancelled" },
  { key: "running", outcome: "RUNNING", label: "running" },
  { key: "queued", outcome: "QUEUED", label: "queued" },
];

const OUTCOME_OF_COUNT: Record<keyof OutcomeCounts, Outcome> = {
  passed: "PASSED",
  failed: "FAILED",
  blocked: "BLOCKED",
  skipped: "SKIPPED",
  error: "ERROR",
  cancelled: "CANCELLED",
  interrupted: "INTERRUPTED",
  precondition_failed: "PRECONDITION_FAILED",
  timed_out: "TIMED_OUT",
  known_defect: "KNOWN_DEFECT",
};

export function countsByOutcome(c: OutcomeCounts | undefined): Partial<Record<Outcome, number>> {
  const out: Partial<Record<Outcome, number>> = {};
  if (c) for (const k of Object.keys(OUTCOME_OF_COUNT) as Array<keyof OutcomeCounts>) out[OUTCOME_OF_COUNT[k]] = c[k] ?? 0;
  return out;
}

export function segmentTone(outcome: Outcome | "RUNNING" | "QUEUED"): Tone {
  if (outcome === "RUNNING") return "blue";
  if (outcome === "QUEUED") return "slate";
  return OUTCOME_META[outcome].tone;
}
